import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, SearchField, Table } from '@factorialco/deprecated-design-system'
import type { TeamDetailFragment, TeamMembershipFragment } from 'generated/resources/graphql'
import _ from 'lodash'

import { ExplotableList } from 'modules/core/components/GroupingExplotable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import TeamsMembersModal from 'modules/teams/components/MembersModal'
import { isActiveMember } from 'modules/teams/lib/isActiveMember'

import BlankState from './BlankState'
import ExpandableGroup from './ExpandableGroup'
import TableHeader from './_Header'
import Item from './_Item'

interface Props {
  team: TeamDetailFragment
  readonly?: boolean
  hasDevJobCatalogTree: boolean
  canCreateJob: boolean
}

type MembershipsPerRole = Record<string, TeamMembershipFragment[]>

const getEmployeeFullName = (employee: TeamMembershipFragment['employee']) => {
  return `${employee.firstName} ${employee.lastName}`
}

const getEmployeeRole = (employee: TeamMembershipFragment['employee']) => {
  return employee.referenceContract?.jobCatalogLevel?.role
}

const TeamList = ({ team, readonly, hasDevJobCatalogTree, canCreateJob }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)

  // Filter to only non-lead members that are not terminated (or have future termination date)
  const members = useMemo(
    () => team.memberships.filter((m) => !m.lead && isActiveMember(m)),
    [team.memberships]
  )

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  // Sort and filter members
  const sortedAndFilteredMembers = useMemo(() => {
    const filtered = members.filter((m) =>
      matchText(debouncedSearch, getEmployeeFullName(m.employee))
    )
    return _.orderBy(filtered, [(m) => m.employee.firstName], ['asc'])
  }, [members, debouncedSearch])

  // Group members by job role
  const membershipsPerJobRole = useMemo(() => {
    return sortedAndFilteredMembers.reduce<MembershipsPerRole>((acc, membership) => {
      const role = getEmployeeRole(membership.employee)
      const key = role ? role.id : 'undefined'
      const current = acc[key] || []
      return {
        ...acc,
        [key]: [...current, membership],
      }
    }, {})
  }, [sortedAndFilteredMembers])

  const roles = useMemo(() => {
    return _.uniqBy(
      sortedAndFilteredMembers.map((m) => getEmployeeRole(m.employee)),
      (role) => role?.id ?? 'undefined'
    )
  }, [sortedAndFilteredMembers])

  const unassignedRoleMembers = useMemo(() => {
    return membershipsPerJobRole['undefined'] || []
  }, [membershipsPerJobRole])

  const unassignedRoleEmployeesIds = useMemo(() => {
    return unassignedRoleMembers
      .filter((m) => m.employee._policies?.assignJobs?.can)
      .map((m) => m.employee.id)
  }, [unassignedRoleMembers])

  const tableOptions = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const handleBulkEdit = () => {
    navigate(
      `${navigation.employees.jobCatalog.bulkEdit.fullPath()}?employeesIds=${JSON.stringify(unassignedRoleEmployeesIds)}&state=unnasigned`
    )
    track(trackingEvents.teams.createJobThroughTeamList)
  }

  const rolesGroups = roles.map((role) => {
    const id = role?.id
    const key = id ?? 'undefined'
    const roleMemberships = membershipsPerJobRole[key] ?? []

    if (!sortedAndFilteredMembers.length) {
      return null
    }

    if (hasDevJobCatalogTree) {
      return (
        // TODO: Remove Number(id) — `TableProps.key` shadows native `React.Key`.
        <Table noRadius hideBorder key={Number(id)}>
          <Table.Body>
            {roleMemberships.map((membership) => {
              return (
                <Item
                  key={membership.employee.id}
                  membership={membership}
                  readonly={readonly}
                  hasDevJobCatalogTree={hasDevJobCatalogTree}
                />
              )
            })}
          </Table.Body>
        </Table>
      )
    }

    return (
      <ExpandableGroup
        key={key}
        id={id}
        count={roleMemberships.length}
        name={role?.name ?? i18n.t('teams.unassigned_role')}
        onBulkEdit={handleBulkEdit}
        canAssingJobs={!!unassignedRoleEmployeesIds?.length && canCreateJob}
        teamId={team.id}
      >
        {roleMemberships.map((membership) => {
          return <Item key={membership.employee.id} membership={membership} readonly={readonly} />
        })}
      </ExpandableGroup>
    )
  })

  return (
    <>
      <Table options={tableOptions} overflowY="auto" noBottomRadius fullWidth>
        <Table noRadius hideBorder>
          <TableHeader hasDevJobCatalogTree={hasDevJobCatalogTree} />
        </Table>
        <Table noRadius hideBorder>
          <BlankState
            show={!sortedAndFilteredMembers.length}
            readonly={readonly}
            onAddMembers={() => setModalOpen(true)}
          />
          <ExplotableList>{rolesGroups}</ExplotableList>
        </Table>
      </Table>
      {modalOpen && (
        <TeamsMembersModal
          selectedGroup="members"
          onClose={() => setModalOpen(false)}
          team={team}
        />
      )}
    </>
  )
}

export default TeamList
